import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, X } from 'lucide-react';
import {
  Field,
  TextInput,
  SelectInput,
  TextareaInput,
  Switch,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../ui/Field';
import { COUNTRIES, getStatesProvinces } from '../../data/geo-data';
import type { Company } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// CompanyForm — Operations Deck redesign
//
// Structure (top-down):
//   • Sticky header with subtitle, Fraunces title, status switch, close
//   • Scrollable body grouped into 3 sections + Notes:
//       01 · Identity (name, registration #)
//       02 · Address  (country/state cascade, city, postal, street, tel)
//       03 · Primary contact (name, initials, email)
//       Notes (full-width textarea)
//   • Sticky footer with Cancel + Save
//
// All inputs read design tokens via the Field primitives so the
// look stays consistent with the rest of the app.
// ─────────────────────────────────────────────────────────────

const emptyCompany: Partial<Company> = {
  companyName: '',
  registrationNumber: '',
  address: '',
  city: '',
  stateProvince: '',
  postalZipCode: '',
  country: '',
  tel: '',
  contactPersonFirstName: '',
  contactPersonLastName: '',
  contactPersonInitials: '',
  contactPersonEmail: '',
  notes: '',
  inactive: false,
};

export interface CompanyErrors {
  companyName?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalZipCode?: string;
  country?: string;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
  contactPersonEmail?: string;
}

function validate(form: Partial<Company>): CompanyErrors {
  const e: CompanyErrors = {};
  if (!form.companyName?.trim()) e.companyName = 'Company name is required';
  if (!form.address?.trim()) e.address = 'Street address is required';
  if (!form.city?.trim()) e.city = 'City is required';
  if (!form.stateProvince?.trim()) e.stateProvince = 'State / Province is required';
  if (!form.postalZipCode?.trim()) e.postalZipCode = 'Postal / Zip is required';
  if (!form.country?.trim()) e.country = 'Country is required';
  if (!form.contactPersonFirstName?.trim()) e.contactPersonFirstName = 'First name is required';
  if (!form.contactPersonLastName?.trim()) e.contactPersonLastName = 'Last name is required';
  if (!form.contactPersonEmail?.trim()) e.contactPersonEmail = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactPersonEmail))
    e.contactPersonEmail = 'Enter a valid email address';
  return e;
}

interface CompanyFormProps {
  open: boolean;
  editing: Company | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (form: Partial<Company>) => Promise<void> | void;
}

export function CompanyForm({ open, editing, saving, onCancel, onSave }: CompanyFormProps) {
  const [form, setForm] = useState<Partial<Company>>(emptyCompany);
  const [errors, setErrors] = useState<CompanyErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // Reset/hydrate when modal opens or target changes.
  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...editing } : { ...emptyCompany });
    setErrors({});
    setSubmitted(false);
  }, [open, editing]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes; auto-focus first field on open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('[data-field="companyName"] input');
      el?.focus();
    }, 60);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, saving, onCancel]);

  if (!open) return null;

  const set = <K extends keyof Company>(key: K, value: Company[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const onCountryChange = (country: string) => {
    setForm((p) => ({ ...p, country, stateProvince: '' }));
    if (submitted) {
      setErrors(validate({ ...form, country, stateProvince: '' }));
    }
  };

  const states = getStatesProvinces(form.country ?? '');
  const hasStateList = states.length > 0;

  const handleSubmit = async () => {
    setSubmitted(true);
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      // Scroll the first errored field into view.
      const firstKey = Object.keys(e)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    await onSave(form);
  };

  // Re-run validation on change after first submit, so errors clear as user fixes them.
  const liveValidate = (next: Partial<Company>) => {
    if (submitted) setErrors(validate(next));
  };

  const subtitle = editing ? `Companies · Editing #${editing.id ?? ''}` : 'Companies · New entry';
  const title = editing ? 'Edit company' : 'Register company';
  const errorCount = Object.keys(errors).length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 760,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <Building2 size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="company-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 520,
                }}
              >
                Tenant directory entry. Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
            <button
              onClick={onCancel}
              aria-label="Close"
              disabled={saving}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-dim)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <X size={18} />
            </button>
            <Switch
              checked={!form.inactive}
              onChange={(active) => set('inactive', !active)}
              label={form.inactive ? 'Inactive' : 'Active'}
              description={form.inactive ? 'Hidden from active list' : 'Visible to users'}
            />
          </div>
        </header>

        {/* ── Error summary ──────────────────────────────────── */}
        {submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>— please review the highlighted inputs.</span>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────── */}
        <div
          style={{
            padding: '22px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 26,
            background: 'var(--color-panel)',
          }}
        >
          {/* 01 Identity */}
          <FormSection number={1} title="Identity">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Company name" required error={submitted ? errors.companyName : undefined}>
                <div data-field="companyName">
                  <TextInput
                    value={form.companyName ?? ''}
                    placeholder="e.g. Quantum Energy Technologies"
                    error={submitted && !!errors.companyName}
                    onChange={(e) => {
                      set('companyName', e.target.value);
                      liveValidate({ ...form, companyName: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field label="Registration number">
                <TextInput
                  value={form.registrationNumber ?? ''}
                  placeholder="123456789"
                  onChange={(e) => set('registrationNumber', e.target.value)}
                />
              </Field>
            </div>
          </FormSection>

          {/* 02 Address */}
          <FormSection number={2} title="Address">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Country" required error={submitted ? errors.country : undefined}>
                <div data-field="country">
                  <SelectInput
                    value={form.country ?? ''}
                    error={submitted && !!errors.country}
                    onChange={(e) => onCountryChange(e.target.value)}
                  >
                    <option value="">— Select country —</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </Field>
              <Field
                label="State / Province"
                required
                error={submitted ? errors.stateProvince : undefined}
              >
                <div data-field="stateProvince">
                  {hasStateList ? (
                    <SelectInput
                      value={form.stateProvince ?? ''}
                      error={submitted && !!errors.stateProvince}
                      onChange={(e) => {
                        set('stateProvince', e.target.value);
                        liveValidate({ ...form, stateProvince: e.target.value });
                      }}
                    >
                      <option value="">— Select state / province —</option>
                      {states.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </SelectInput>
                  ) : (
                    <TextInput
                      value={form.stateProvince ?? ''}
                      placeholder={form.country ? 'Enter state / province' : 'Select country first'}
                      disabled={!form.country}
                      error={submitted && !!errors.stateProvince}
                      onChange={(e) => {
                        set('stateProvince', e.target.value);
                        liveValidate({ ...form, stateProvince: e.target.value });
                      }}
                    />
                  )}
                </div>
              </Field>
              <Field label="City" required error={submitted ? errors.city : undefined}>
                <div data-field="city">
                  <TextInput
                    value={form.city ?? ''}
                    placeholder="e.g. Calgary"
                    error={submitted && !!errors.city}
                    onChange={(e) => {
                      set('city', e.target.value);
                      liveValidate({ ...form, city: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field
                label="Postal / Zip"
                required
                error={submitted ? errors.postalZipCode : undefined}
              >
                <div data-field="postalZipCode">
                  <TextInput
                    value={form.postalZipCode ?? ''}
                    placeholder="T2P 1J9 · 77002"
                    error={submitted && !!errors.postalZipCode}
                    onChange={(e) => {
                      set('postalZipCode', e.target.value);
                      liveValidate({ ...form, postalZipCode: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field
                label="Street address"
                required
                error={submitted ? errors.address : undefined}
                style={{ gridColumn: '1 / -1' } as never}
              >
                <div data-field="address">
                  <TextInput
                    value={form.address ?? ''}
                    placeholder="123 Energy Way"
                    error={submitted && !!errors.address}
                    onChange={(e) => {
                      set('address', e.target.value);
                      liveValidate({ ...form, address: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field label="Telephone">
                <TextInput
                  value={form.tel ?? ''}
                  placeholder="+1 (281) 555-0100"
                  type="tel"
                  onChange={(e) => set('tel', e.target.value)}
                />
              </Field>
            </div>
          </FormSection>

          {/* 03 Primary contact */}
          <FormSection number={3} title="Primary contact" highlight>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="First name"
                required
                error={submitted ? errors.contactPersonFirstName : undefined}
              >
                <div data-field="contactPersonFirstName">
                  <TextInput
                    value={form.contactPersonFirstName ?? ''}
                    placeholder="Dave"
                    error={submitted && !!errors.contactPersonFirstName}
                    onChange={(e) => {
                      set('contactPersonFirstName', e.target.value);
                      liveValidate({ ...form, contactPersonFirstName: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field
                label="Last name"
                required
                error={submitted ? errors.contactPersonLastName : undefined}
              >
                <div data-field="contactPersonLastName">
                  <TextInput
                    value={form.contactPersonLastName ?? ''}
                    placeholder="Cooper"
                    error={submitted && !!errors.contactPersonLastName}
                    onChange={(e) => {
                      set('contactPersonLastName', e.target.value);
                      liveValidate({ ...form, contactPersonLastName: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field label="Initials" hint="2 or 3 characters, e.g. DRC.">
                <TextInput
                  value={form.contactPersonInitials ?? ''}
                  placeholder="DRC"
                  maxLength={5}
                  onChange={(e) => set('contactPersonInitials', e.target.value.toUpperCase())}
                />
              </Field>
              <Field
                label="Email"
                required
                error={submitted ? errors.contactPersonEmail : undefined}
              >
                <div data-field="contactPersonEmail">
                  <TextInput
                    type="email"
                    value={form.contactPersonEmail ?? ''}
                    placeholder="dave.cooper@example.com"
                    error={submitted && !!errors.contactPersonEmail}
                    onChange={(e) => {
                      set('contactPersonEmail', e.target.value);
                      liveValidate({ ...form, contactPersonEmail: e.target.value });
                    }}
                  />
                </div>
              </Field>
            </div>
          </FormSection>

          {/* Notes */}
          <FormSection title="Notes">
            <TextareaInput
              value={form.notes ?? ''}
              placeholder="Anything the team should know about this company…"
              rows={4}
              onChange={(e) => set('notes', e.target.value)}
            />
          </FormSection>
        </div>

        {/* ── Sticky footer ──────────────────────────────────── */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div style={{ fontSize: 11.5, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', letterSpacing: 0.6 }}>
            {editing ? `LAST UPDATE · ${formatTs(editing.lastModifiedDate ?? editing.createdDate)}` : 'NEW RECORD'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Register company'}
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ── helpers ──────────────────────────────────────────────────
function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}
